// Administrators — who may use the admin console, and in which role.
//
// identity.platform_admin is the authZ gate; Keycloak is authN only (Sleek owns authZ). You
// cannot grant someone who has never signed in — there is no account to attach the grant to. The
// flow is: they sign in once (and are refused), which provisions their account, then you grant
// them here, then they sign in again. The API says exactly that on a 404, so we surface its text.
//
// The API also refuses to disable/demote the last active super admin, or to disable yourself; both
// come back as a 400 with a message, which we show inline on the row rather than re-implementing.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { focusModal } from '../shared/modal.js';
import { searchSelect } from '../shared/searchselect.js';

// Mirrors the identity.platform_admin role CHECK — super | billing_admin | billing_readonly.
export const ROLES = [
  { value: 'super', label: 'Super admin',
    hint: 'Full access, including granting and revoking administrators.' },
  { value: 'billing_admin', label: 'Billing admin',
    hint: 'Manage billing, pricing tiers and subscriptions.' },
  { value: 'billing_readonly', label: 'Billing (read only)',
    hint: 'View billing. Cannot change anything.' },
];

export const roleLabel = value => (ROLES.find(r => r.value === value) || {}).label || value || '';
export const roleHint = value => (ROLES.find(r => r.value === value) || {}).hint || '';

// added_at is an ISO timestamp from the API — show the date part only.
export function addedOn(value) {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d) ? String(value).slice(0, 10) : d.toISOString().slice(0, 10);
}

export function administratorsScreen() {
  const rows = signal([]);
  const loading = signal(true);
  const loadErr = signal('');

  // Grant / change-role form. Lives in the STATIC template (inputs must never sit inside a
  // ${() => …} block) and is shown via ?hidden.
  const formOpen = signal(false);
  const editingId = signal(null);          // null = granting someone new
  const fEmail = signal(''), fName = signal(''), fPass = signal('');   // one admin type — Super Admin, always
  const formErr = signal(''), saving = signal(false), invalid = signal({});
  // Email-first flow: after the admin types the email we check whether it already has a Sleek
  // account. emailExists: null = not checked yet, true = existing (name/password stay DISABLED,
  // grant only), false = brand-new (name/password ENABLED so a login is provisioned on grant).
  const emailExists = signal(null), checking = signal(false), existingName = signal('');
  let checkTimer;

  // Per-row enable/disable.
  const confirmId = signal(null), busyId = signal(null), rowErr = signal('');

  // Oversight grants (#8/#9): which companies / users an admin oversees on the tenant side. Managed
  // in a per-admin modal. The two pickers are searchSelect comboboxes built ONCE here and moved into
  // the modal by node interpolation (never rebuilt inside a reactive slot — the home.js pattern).
  const grantsFor = signal(null);        // the admin {user_id, email} whose oversight modal is open
  const grants = signal({ companies: [], users: [] });
  const grantsLoading = signal(false), grantsErr = signal(''), grantBusy = signal('');
  const companyPick = searchSelect({
    placeholder: '— Add a company —', ariaLabel: 'Company to oversee', className: 'form-control',
    onSelect: v => { if (v) addGrant({ company_id: Number(v) }); },
  });
  const userPick = searchSelect({
    placeholder: '— Add a user (all their companies) —', ariaLabel: 'User to oversee',
    className: 'form-control',
    onSelect: v => { if (v) addGrant({ user_id: Number(v) }); },
  });

  async function load() {
    loading.value = true; loadErr.value = '';
    try {
      const r = await api.get('/admin/administrators');
      rows.value = r.data || [];
    } catch (err) {
      rows.value = [];
      loadErr.value = (err.data && err.data.error) || err.message || 'Could not load administrators';
    }
    loading.value = false;
  }
  load();

  const clearInvalid = k => { if (invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };

  // Reset the email-existence check (any edit invalidates the last answer).
  function resetEmailCheck() {
    clearTimeout(checkTimer); checking.value = false; emailExists.value = null; existingName.value = '';
  }
  // Ask the API whether this email already has a Sleek account. Debounced from the input handler.
  async function runEmailCheck(raw) {
    const email = (raw || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { resetEmailCheck(); return; }
    checking.value = true;
    try {
      const r = await api.get('/admin/users/exists?email=' + encodeURIComponent(email));
      if (fEmail.value.trim().toLowerCase() !== email) return;   // field moved on — drop stale answer
      emailExists.value = !!(r && r.exists);
      existingName.value = (r && r.display_name) || '';
    } catch {
      if (fEmail.value.trim().toLowerCase() !== email) return;
      emailExists.value = false;                 // check failed — let them proceed as new; API backstops
      existingName.value = '';
    }
    checking.value = false;
  }
  function onEmailInput(e) {
    fEmail.value = e.target.value; clearInvalid('email');
    emailExists.value = null; existingName.value = '';         // invalidate until the new check lands
    clearTimeout(checkTimer);
    checkTimer = setTimeout(() => runEmailCheck(fEmail.value), 350);
  }

  function openGrant() {
    editingId.value = null; fEmail.value = ''; fName.value = ''; fPass.value = '';
    formErr.value = ''; invalid.value = {}; resetEmailCheck(); formOpen.value = true; focusModal();
  }
  function closeForm() { formOpen.value = false; }

  async function save(e) {
    e.preventDefault();
    const email = fEmail.value.trim().toLowerCase();
    const isNew = !editingId.value && emailExists.value === false;   // checked AND brand-new
    const inv = {};
    if (!editingId.value && (!email || !email.includes('@'))) inv.email = true;
    if (isNew && !fName.value.trim()) inv.name = true;               // a new login needs a name…
    if (isNew && (!fPass.value || fPass.value.length < 8)) inv.pass = true;   // …and a temp password
    invalid.value = inv;
    if (Object.keys(inv).length) { formErr.value = 'Please fill in the highlighted fields.'; return; }

    saving.value = true; formErr.value = '';
    try {
      if (isNew) {
        await api.post('/admin/administrators', { email, role: 'super',
          display_name: fName.value.trim(), temp_password: fPass.value });
      } else {
        // existing Sleek user (a tenant user, a company owner, or an admin) — grant Super Admin;
        // their name/password are left untouched.
        await api.post('/admin/administrators', { email, role: 'super' });
      }
      formOpen.value = false;
      await load();
    } catch (err) {
      formErr.value = (err.data && err.data.error) || err.message || 'Save failed';
    }
    saving.value = false;
  }

  function armDisable(id) { rowErr.value = ''; confirmId.value = id; }
  function cancelDisable() { confirmId.value = null; rowErr.value = ''; }

  async function setActive(a, isActive) {
    busyId.value = a.user_id; rowErr.value = '';
    try {
      await api.patch('/admin/administrators/' + a.user_id, { is_active: isActive });
      confirmId.value = null;
      await load();
    } catch (err) {
      // the last-active-super / self-disable guardrails land here — show the reason on the row
      rowErr.value = (err.data && err.data.error) || err.message || 'Could not update';
      confirmId.value = a.user_id;
    }
    busyId.value = null;
  }

  // ── oversight modal (#8/#9) ──
  async function loadGrants(uid) {
    grantsLoading.value = true; grantsErr.value = '';
    try { grants.value = await api.get('/admin/administrators/' + uid + '/grants'); }
    catch (err) { grants.value = { companies: [], users: [] };
      grantsErr.value = (err.data && err.data.error) || err.message || 'Could not load assignments'; }
    grantsLoading.value = false;
  }
  async function loadPickerOptions() {
    try { const r = await api.get('/admin/companies?per_page=200');
      companyPick.setOptions((r.data || []).map(c => ({ value: String(c.id), label: c.name || ('Company #' + c.id) }))); } catch { /* leave empty */ }
    try { const r = await api.get('/admin/users?per_page=200');
      userPick.setOptions((r.data || []).map(u => ({ value: String(u.id),
        label: (u.display_name ? u.display_name + ' · ' : '') + (u.email || ('User #' + u.id)) }))); } catch { /* leave empty */ }
  }
  function openOversight(a) {
    grantsFor.value = a; grants.value = { companies: [], users: [] };
    companyPick.set(''); userPick.set('');
    loadGrants(a.user_id); loadPickerOptions(); focusModal();
  }
  function closeOversight() { grantsFor.value = null; }
  async function addGrant(body) {
    const a = grantsFor.value; if (!a) return;
    grantBusy.value = 'add'; grantsErr.value = '';
    try {
      await api.post('/admin/administrators/' + a.user_id + '/grants', body);
      companyPick.set(''); userPick.set('');
      await loadGrants(a.user_id);
    } catch (err) { grantsErr.value = (err.data && err.data.error) || err.message || 'Could not assign'; }
    grantBusy.value = '';
  }
  async function removeGrant(grantId) {
    const a = grantsFor.value; if (!a) return;
    grantBusy.value = 'g' + grantId; grantsErr.value = '';
    try {
      await api.del('/admin/administrators/' + a.user_id + '/grants/' + grantId);
      await loadGrants(a.user_id);
    } catch (err) { grantsErr.value = (err.data && err.data.error) || err.message || 'Could not remove'; }
    grantBusy.value = '';
  }

  // ONE reactive block: every signal is read at the top and used as a plain value below, because
  // a nested ${() => …} inside a reactive slot appends its output on re-render.
  const list = () => {
    if (loading.value) return html`<div class="muted-block">Loading…</div>`;
    if (loadErr.value) return html`<div class="alert alert-danger py-2">${loadErr.value}</div>`;
    if (!rows.value.length) {
      return html`<div class="muted-block">No administrators yet.</div>`;
    }
    const cId = confirmId.value, bId = busyId.value, err = rowErr.value;
    return html`<div class="row-list">${rows.value.map(a => {
      const armed = cId === a.user_id;
      const busy = bId === a.user_id;
      const blocked = armed && err;
      return html`
        <div class=${'row-card' + (a.is_active ? '' : ' inactive') + (blocked ? ' row-blocked' : '')}>
          <span class="row-main">
            <b>${a.email}</b>
            ${a.display_name ? html`<span class="muted"> — ${a.display_name}</span>` : ''}
            ${a.is_active ? '' : html`<span class="tag-off">disabled</span>`}
          </span>
          <span class="badge badge-light">${roleLabel(a.role)}</span>
          <span class="muted added-on">added ${addedOn(a.added_at)}</span>
          ${blocked
            ? html`<span class="confirm-inline confirm-err">
                <span class="confirm-errmsg">⚠ ${err}</span>
                <button type="button" class="btn-confirm-no" @click=${cancelDisable}>OK</button>
              </span>`
            : ''}
          <button type="button" class="btn btn-sm btn-outline" title="Companies & users this admin oversees"
                  @click=${() => openOversight(a)}>Oversees</button>
          ${!a.is_active
            ? html`<button type="button" class="btn btn-sm btn-outline" ?disabled=${busy}
                           @click=${() => setActive(a, true)}>${busy ? '…' : 'Enable'}</button>`
            : (armed && !err
                ? html`<span class="confirm-inline">
                    <span class="confirm-q">Disable?</span>
                    <button type="button" class="btn-confirm-yes" ?disabled=${busy}
                            @click=${() => setActive(a, false)}>${busy ? '…' : 'Yes'}</button>
                    <button type="button" class="btn-confirm-no" @click=${cancelDisable}>No</button>
                  </span>`
                : html`<button type="button" class="btn btn-sm btn-outline"
                               @click=${() => armDisable(a.user_id)}>Disable</button>`)}
        </div>`;
    })}</div>`;
  };

  // Current assignments inside the oversight modal — ONE reactive block (all signals read at top).
  const grantsList = () => {
    if (grantsLoading.value) return html`<div class="muted-block">Loading…</div>`;
    const g = grants.value || {}, bz = grantBusy.value;
    const co = g.companies || [], us = g.users || [];
    if (!co.length && !us.length) return html`<div class="muted-block">Nothing assigned yet.</div>`;
    return html`<div class="row-list grants-list">
      ${us.map(u => html`
        <div class="row-card">
          <span class="row-main"><b>${u.display_name || u.email}</b>
            <span class="muted"> — user · ${u.company_count} ${u.company_count === 1 ? 'company' : 'companies'}</span></span>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${bz === 'g' + u.grant_id}
                  @click=${() => removeGrant(u.grant_id)}>${bz === 'g' + u.grant_id ? '…' : 'Remove'}</button>
        </div>`)}
      ${co.map(c => html`
        <div class="row-card">
          <span class="row-main"><b>${c.company_name}</b><span class="muted"> — company</span></span>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${bz === 'g' + c.grant_id}
                  @click=${() => removeGrant(c.grant_id)}>${bz === 'g' + c.grant_id ? '…' : 'Remove'}</button>
        </div>`)}
    </div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">Administrators</h1>
          <div class="page-sub">Who may use this console, and in which role</div>
        </div>
        <button class="btn btn-primary" @click=${openGrant}>+ Grant admin</button>
      </div>
      <div class="form-note">
        Grant admin by email. If they're already a Sleek user — or a company owner — they're
        granted at once; if the email is new, set a temporary password and we'll create their
        login for them to change at first sign-in.
      </div>
      ${list}

      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head">
            <h3>Grant admin access</h3>
            <button type="button" class="modal-x" @click=${closeForm}>✕</button>
          </div>
          <label class="form-label">Email</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.email ? ' is-invalid' : '')}
                 type="email" .value=${fEmail} ?disabled=${() => !!editingId.value}
                 @input=${onEmailInput}
                 placeholder="e.g. someone@aboo.co.za">
          <div ?hidden=${() => !!editingId.value}>
            ${() => {
              if (checking.value) return html`<div class="form-note">Checking this email…</div>`;
              if (emailExists.value === true)
                return html`<div class="form-note"><b>✓ Already a Sleek user${existingName.value ? ' — ' + existingName.value : ''}.</b> Click Add — they get Super Admin access; no name or password needed.</div>`;
              if (emailExists.value === false)
                return html`<div class="form-note"><b>New email.</b> Set a name and a password to create their login — they can change it later.</div>`;
              return html`<div class="form-note">Enter the email first — we'll check whether they already have a Sleek account.</div>`;
            }}
            <label class="form-label">Display name</label>
            <input class=${() => 'form-control mb-2' + (invalid.value.name ? ' is-invalid' : '')}
                   .value=${fName} ?disabled=${() => checking.value || emailExists.value !== false}
                   @input=${e => { fName.value = e.target.value; clearInvalid('name'); }}
                   placeholder="e.g. Jane Mokoena">
            <label class="form-label">Password</label>
            <input class=${() => 'form-control mb-1' + (invalid.value.pass ? ' is-invalid' : '')}
                   type="password" .value=${fPass} ?disabled=${() => checking.value || emailExists.value !== false}
                   @input=${e => { fPass.value = e.target.value; clearInvalid('pass'); }}
                   placeholder="e.g. at least 8 characters">
          </div>
          <div class="form-note mb-2">They'll be added as a <b>Super Admin</b> — full console access.</div>
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr.value}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary"
                    ?disabled=${() => saving.value || (!editingId.value && (checking.value || emailExists.value === null))}>
              ${() => saving.value ? 'Saving…' : (emailExists.value === true && !editingId.value ? 'Add' : 'Save')}</button>
          </div>
        </form>
      </div>

      <div class="modal-overlay" ?hidden=${() => !grantsFor.value}>
        <div class="modal-card">
          <div class="modal-head">
            <h3>Oversees${() => grantsFor.value ? ' — ' + grantsFor.value.email : ''}</h3>
            <button type="button" class="modal-x" @click=${closeOversight}>✕</button>
          </div>
          <div class="form-note">
            Companies this admin oversees on the tenant side. When they sign in on the companies
            side, these appear on their company splash. Assigning a user includes all that user's
            companies.
          </div>
          <label class="form-label">Add a company</label>
          <div class="mb-2">${companyPick.el}</div>
          <label class="form-label">Add a user (all their companies)</label>
          <div class="mb-2">${userPick.el}</div>
          ${() => grantsErr.value ? html`<div class="alert alert-danger py-2 mb-2">${grantsErr.value}</div>` : null}
          ${grantsList}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-primary" @click=${closeOversight}>Done</button>
          </div>
        </div>
      </div>
    </div>`;

  return shell('administrators', 'Administrators', content);
}
